import { Game } from '../types/curling';
import { fitSmoothCurve, fitQuadraticBaseline } from './curveFit';

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// A bucket needs this many shots before outlier rejection is trusted to tell
// a blunder from real spread.
const MIN_SAMPLES_FOR_OUTLIER_REJECTION = 5;
// ~3x the median absolute deviation is roughly a 95% band on a normal-ish
// spread — wide enough to leave ordinary player variance alone and only
// catch the genuine tail blunders.
const OUTLIER_MAD_MULTIPLIER = 3;

/**
 * Drops blunder values from a single position/end bucket before it gets
 * averaged: once a bucket actually has enough shots to tell the difference,
 * anything more than OUTLIER_MAD_MULTIPLIER median-absolute-deviations from
 * the bucket's own median is treated as a mis-recorded or wildly mis-thrown
 * shot rather than real ice or player signal. What was actually thrown still
 * drives every average that survives this — this only trims the blunders,
 * it never substitutes the called/expected value for what happened.
 */
function excludeOutliers(values: number[]): number[] {
  if (values.length < MIN_SAMPLES_FOR_OUTLIER_REJECTION) return values;
  const med = median(values);
  const mad = median(values.map(v => Math.abs(v - med)));
  if (mad === 0) return values;
  return values.filter(v => Math.abs(v - med) <= OUTLIER_MAD_MULTIPLIER * mad);
}

export interface PositionCurl {
  id: string;
  position: number;
  curl: number;
}

/** Every non-skipped shot's curl against its actual broom position, split by end parity. */
export function buildCurlByPosition(game: Game): { away: PositionCurl[]; home: PositionCurl[] } {
  const away: PositionCurl[] = [];
  const home: PositionCurl[] = [];
  let awayId = 0;
  let homeId = 0;

  game.ends.forEach(end => {
    const isAway = end.number % 2 !== 0;
    end.shots.forEach(shot => {
      if (shot.skipped) return;
      const entry = { id: `curlpos-${isAway ? awayId++ : homeId++}`, position: shot.broomPosition, curl: shot.curl };
      (isAway ? away : home).push(entry);
    });
  });

  return { away, home };
}

/** Averages every non-skipped shot's curl per end, across the whole game. */
export function averageCurlByEnd(game: Game): { x: number; y: number }[] {
  const bucketed = new Map<number, number[]>();
  game.ends.forEach(end => {
    end.shots.forEach(shot => {
      if (shot.skipped) return;
      if (!bucketed.has(end.number)) bucketed.set(end.number, []);
      bucketed.get(end.number)!.push(shot.curl);
    });
  });
  return Array.from(bucketed.entries())
    .map(([number, curls]) => { const kept = excludeOutliers(curls); return { x: number, y: kept.reduce((sum, c) => sum + c, 0) / kept.length }; })
    .sort((a, b) => a.x - b.x);
}

/**
 * Buckets shots to the nearest foot of broom position within [-6, 6] and
 * averages their curl — same bucketing SheetMap's Curl by Path chart uses,
 * so the two pages read consistently against each other.
 */
export function averageByFoot(data: PositionCurl[]): { x: number; y: number }[] {
  const bucketed = new Map<number, number[]>();
  data.forEach(d => {
    const bucket = Math.max(-6, Math.min(6, Math.round(d.position)));
    if (!bucketed.has(bucket)) bucketed.set(bucket, []);
    bucketed.get(bucket)!.push(d.curl);
  });
  return Array.from(bucketed.entries())
    .map(([position, curls]) => { const kept = excludeOutliers(curls); return { x: position, y: kept.reduce((sum, c) => sum + c, 0) / kept.length }; })
    .sort((a, b) => a.x - b.x);
}

export interface DeviationStats {
  deviation: { x: number; y: number }[];
  raw: { x: number; y: number }[];
  average: number;
  maxVariance: number;
  minVariance: number;
}

/**
 * Shared by every "normalized" chart on the sheet-scoring page (curl or
 * weight, by position or by end): fits a smooth expected baseline through the
 * given points and returns each point's departure from it, plus the plain
 * average and the best/worst departures — the exact numbers each chart plots,
 * pulled out once so both the chart and its score grade the same thing.
 */
export function computeDeviationStats(points: { x: number; y: number }[]): DeviationStats | null {
  const baseline = fitQuadraticBaseline(points);
  if (!baseline) return null;
  const deviation = points.map(p => ({ x: p.x, y: p.y - baseline(p.x) }));
  const values = points.map(p => p.y);
  const average = values.reduce((sum, v) => sum + v, 0) / values.length;
  const variances = deviation.map(p => p.y);
  return { deviation, raw: points, average, maxVariance: Math.max(...variances), minVariance: Math.min(...variances) };
}

export function rootMeanSquare(values: number[]): number {
  if (values.length === 0) return 0;
  return Math.sqrt(values.reduce((sum, v) => sum + v * v, 0) / values.length);
}

/** 10 at zero deviation, sliding down to 0 by the time it's `maxRms` off. */
export function rmsToScore(rms: number, maxRms: number): number {
  return Math.max(0, Math.min(10, 10 * (1 - rms / maxRms)));
}

/** 10 anywhere inside [lo, hi], sliding down to 0 by `tolerance` past the nearer edge. */
export function targetBandScore(value: number, lo: number, hi: number, tolerance: number): number {
  if (value >= lo && value <= hi) return 10;
  const distance = value < lo ? lo - value : value - hi;
  return Math.max(0, Math.min(10, 10 * (1 - distance / tolerance)));
}

/**
 * How closely the away and home variance curves (curl or weight, both plotted
 * against the same broom-position axis) match in shape, over whatever
 * position range both actually have data for. Too little overlap to compare
 * (under 2 ft) reads as "not enough data" rather than a score, same as
 * everywhere else on this page that declines to guess.
 */
export function shapeSimilarityScore(awayStats: DeviationStats | null, homeStats: DeviationStats | null, maxRms: number): number | null {
  if (!awayStats || !homeStats || awayStats.deviation.length === 0 || homeStats.deviation.length === 0) return null;
  const awayCurve = fitSmoothCurve(awayStats.deviation);
  const homeCurve = fitSmoothCurve(homeStats.deviation);
  if (!awayCurve || !homeCurve) return null;

  const lo = Math.max(awayStats.deviation[0].x, homeStats.deviation[0].x);
  const hi = Math.min(awayStats.deviation[awayStats.deviation.length - 1].x, homeStats.deviation[homeStats.deviation.length - 1].x);
  if (hi - lo < 2) return null;

  const steps = 25;
  const diffs = Array.from({ length: steps }, (_, i) => {
    const x = lo + (i / (steps - 1)) * (hi - lo);
    return awayCurve(x) - homeCurve(x);
  });
  return rmsToScore(rootMeanSquare(diffs), maxRms);
}

/**
 * How closely the away and home ends' overall average (curl or weight) match
 * — simpler and more literal than a curve-shape RMS: just the gap between
 * the two averages. Under `threshold` is normal read/entry noise and costs
 * nothing; at or past that, every further `threshold`-sized step of gap
 * costs a further point (e.g. with a 0.25 threshold, a 0.4 gap costs
 * 0.4/0.25 = 1.6 points).
 */
export function averageDifferenceScore(averageA: number | null, averageB: number | null, threshold: number): number | null {
  if (averageA === null || averageB === null) return null;
  const diff = Math.abs(averageA - averageB);
  if (diff < threshold) return 10;
  return Math.max(0, 10 - diff / threshold);
}

export interface TieredDeductionRule {
  /** Deviation from the position-specific expected baseline, above which a path loses 1, then 2, points. */
  minorThreshold: number;
  majorThreshold: number;
  /** Absolute value outside [extremeMin, extremeMax] is a bad reading regardless of what
   *  the baseline expected there, and costs 3 more — independent of, and stackable with,
   *  the deviation deduction above. */
  extremeMin: number;
  extremeMax: number;
}

// Curl deviation in feet; absolute curl under 1 ft or over 7 ft is extreme.
export const CURL_PATH_RULE: TieredDeductionRule = { minorThreshold: 0.5, majorThreshold: 1.0, extremeMin: 1.0, extremeMax: 7.0 };

// An end-parity group (every away end, or every home end, pooled together)
// needs at least this many shots before a flatness score means anything —
// one or two shots can land dead-flat or wildly off by pure chance, and
// tieredPathScore/escalatingPathScore have no other way to tell a real
// pattern from noise this thin.
const MIN_SHOTS_FOR_FLATNESS_SCORE = 8;

export interface EscalatingDeductionRule {
  /** Deviation below this threshold costs nothing. */
  startThreshold: number;
  /** Each further increment of this size past the start threshold costs one more point. */
  stepSize: number;
  /** Same independent, stackable extreme check as TieredDeductionRule. */
  extremeMin: number;
  extremeMax: number;
  extremeDeduction: number;
}

// Weight deviation in seconds: -1 at 0.5s off baseline, -2 at 0.75s, -3 at 1.0s,
// and so on every quarter-second past that. Absolute draw time under 12s or
// over 17s is extreme and costs 3 more, independent of the above.
export const WEIGHT_PATH_RULE: EscalatingDeductionRule = {
  startThreshold: 0.5, stepSize: 0.25, extremeMin: 12.0, extremeMax: 17.0, extremeDeduction: 3,
};

/**
 * One end's flatness, scored path by path (each broom-position foot bucket
 * the chart plots) rather than as a single whole-curve average, so a couple
 * of genuinely bad paths can't get diluted away by a lot of good ones. See
 * TieredDeductionRule for the exact thresholds.
 */
export function tieredPathScore(stats: DeviationStats | null, rule: TieredDeductionRule): number | null {
  if (!stats || stats.deviation.length === 0) return null;
  let deduction = 0;
  stats.deviation.forEach((point, i) => {
    const off = Math.abs(point.y);
    if (off >= rule.majorThreshold) deduction += 2;
    else if (off >= rule.minorThreshold) deduction += 1;

    const raw = stats.raw[i].y;
    if (raw < rule.extremeMin || raw > rule.extremeMax) deduction += 3;
  });
  return Math.max(0, 10 - deduction);
}

/**
 * Same per-path idea as tieredPathScore, but for a rule whose deduction keeps
 * climbing by one point every further step past the start threshold, rather
 * than capping at two fixed tiers.
 */
export function escalatingPathScore(stats: DeviationStats | null, rule: EscalatingDeductionRule): number | null {
  if (!stats || stats.deviation.length === 0) return null;
  let deduction = 0;
  stats.deviation.forEach((point, i) => {
    const off = Math.abs(point.y);
    if (off >= rule.startThreshold) {
      deduction += Math.floor((off - rule.startThreshold) / rule.stepSize) + 1;
    }

    const raw = stats.raw[i].y;
    if (raw < rule.extremeMin || raw > rule.extremeMax) deduction += rule.extremeDeduction;
  });
  return Math.max(0, 10 - deduction);
}

// Draw weight only — mixing in hit weights would swamp any real pace signal
// with shot-type noise.
const DRAW_SHOT_TYPES = new Set([
  'top-twelve', 'top-eight', 'top-four', 'tee-line', 'back-four', 'back-eight', 'back-twelve',
]);

export interface DrawWeightShot {
  id: string;
  position: number;
  turn: string;
  endNumber: number;
  isAway: boolean;
  isTeeLine: boolean;
  time: number;
  expectedTime: number;
  timestamp: number;
}

/** Every non-skipped, timed draw-weight shot in the game. */
export function buildDrawWeightShots(game: Game): DrawWeightShot[] {
  const shots: DrawWeightShot[] = [];
  game.ends.forEach(end => {
    const isAway = end.number % 2 !== 0;
    end.shots.forEach(shot => {
      if (shot.skipped || shot.time <= 0 || !DRAW_SHOT_TYPES.has(shot.shotType)) return;
      shots.push({
        id: shot.id,
        position: shot.broomPosition,
        turn: shot.turn,
        endNumber: end.number,
        isAway,
        isTeeLine: shot.shotType === 'tee-line',
        time: shot.time,
        expectedTime: shot.expectedTime,
        timestamp: shot.timestamp,
      });
    });
  });
  return shots;
}

/**
 * A draw shot's time, re-based onto a tee-line-equivalent scale: how it did
 * relative to its own called weight, applied against the tee-line reference
 * instead of its own zone's. Different draw zones (top-twelve, back-twelve,
 * ...) are called at genuinely different weights, so averaging their raw
 * times together conflates "which zone got called more" with "how the ice is
 * actually running" — this puts every draw on the same footing first.
 */
function toTeeLineNormalizedTime(shot: DrawWeightShot, teeLineReference: number): number {
  return shot.time - shot.expectedTime + teeLineReference;
}

/**
 * Drops the first rock thrown down each specific path (turn + broom position,
 * rounded to the nearest foot) — that rock is expected to run a little slower
 * while it breaks the line in, which isn't a real ice-consistency problem.
 */
export function excludeFirstRockPerPath(shots: DrawWeightShot[]): DrawWeightShot[] {
  const byPath = new Map<string, DrawWeightShot[]>();
  shots.forEach(shot => {
    const key = `${shot.turn}@${Math.round(shot.position)}`;
    if (!byPath.has(key)) byPath.set(key, []);
    byPath.get(key)!.push(shot);
  });

  const excludedIds = new Set<string>();
  byPath.forEach(pathShots => {
    const firstByTime = pathShots.reduce((earliest, s) => (s.timestamp < earliest.timestamp ? s : earliest));
    excludedIds.add(firstByTime.id);
  });

  return shots.filter(shot => !excludedIds.has(shot.id));
}

/** Buckets shots to the nearest foot of broom position and averages their time. */
export function averageTimeByFoot(shots: DrawWeightShot[], teeLineReference: number): { x: number; y: number }[] {
  const bucketed = new Map<number, number[]>();
  shots.forEach(shot => {
    const bucket = Math.max(-6, Math.min(6, Math.round(shot.position)));
    if (!bucketed.has(bucket)) bucketed.set(bucket, []);
    bucketed.get(bucket)!.push(toTeeLineNormalizedTime(shot, teeLineReference));
  });
  return Array.from(bucketed.entries())
    .map(([position, times]) => { const kept = excludeOutliers(times); return { x: position, y: kept.reduce((sum, t) => sum + t, 0) / kept.length }; })
    .sort((a, b) => a.x - b.x);
}

/** Averages tee-line-normalized time per end. */
export function averageTimeByEnd(shots: DrawWeightShot[], teeLineReference: number): { x: number; y: number }[] {
  const bucketed = new Map<number, number[]>();
  shots.forEach(shot => {
    if (!bucketed.has(shot.endNumber)) bucketed.set(shot.endNumber, []);
    bucketed.get(shot.endNumber)!.push(toTeeLineNormalizedTime(shot, teeLineReference));
  });
  return Array.from(bucketed.entries())
    .map(([end, times]) => { const kept = excludeOutliers(times); return { x: end, y: kept.reduce((sum, t) => sum + t, 0) / kept.length }; })
    .sort((a, b) => a.x - b.x);
}

/**
 * Samples a fitted curve only between the lowest and highest x that actually
 * had data — broom positions toward the edges commonly never get a recorded
 * draw, and guessing what the ice does out there by extrapolating is worse
 * than just leaving it blank.
 */
export function sampleWithinKnownRange(points: { x: number; y: number }[], curve: (x: number) => number, steps: number) {
  if (points.length === 0) return [];
  const minX = points[0].x;
  const maxX = points[points.length - 1].x;
  if (minX === maxX) return [{ x: minX, y: curve(minX) }];
  return Array.from({ length: steps }, (_, i) => {
    const x = minX + (i / (steps - 1)) * (maxX - minX);
    return { x, y: curve(x) };
  });
}

export interface SheetSubScore {
  label: string;
  value: number | null;
  description: string;
}

export interface SheetScoring {
  hasData: boolean;
  away: PositionCurl[];
  home: PositionCurl[];
  awayCurlStats: DeviationStats | null;
  homeCurlStats: DeviationStats | null;
  curlByEndStats: DeviationStats | null;
  drawWeightShots: DrawWeightShot[];
  awayWeightShots: DrawWeightShot[];
  homeWeightShots: DrawWeightShot[];
  awayWeightStats: DeviationStats | null;
  homeWeightStats: DeviationStats | null;
  weightByEndStats: DeviationStats | null;
  teeLineReference: number;
  avgTeeLineWeight: number | null;
  curlSubScores: SheetSubScore[];
  weightSubScores: SheetSubScore[];
  subScores: SheetSubScore[];
  /** How many of subScores actually have a value yet — what overallScore is out of. */
  determinedSubScoreCount: number;
  overallScore: number;
  compositeCurlScore: number | null;
  compositeWeightScore: number | null;
}

/**
 * Everything the BRC Scoring "Sheet" page needs, computed once: the raw
 * per-position and per-end curl/weight data each chart plots, and the
 * sub-scores derived from that exact same data, so the numbers on screen and
 * the shapes on screen can never disagree with each other.
 */
export function computeSheetScoring(game: Game): SheetScoring {
  const { away, home } = buildCurlByPosition(game);

  const drawWeightShots = excludeFirstRockPerPath(buildDrawWeightShots(game));
  const awayWeightShots = drawWeightShots.filter(s => s.isAway);
  const homeWeightShots = drawWeightShots.filter(s => !s.isAway);
  const teeLineShots = drawWeightShots.filter(s => s.isTeeLine);
  const avgTeeLineWeight = teeLineShots.length > 0
    ? teeLineShots.reduce((sum, s) => sum + s.time, 0) / teeLineShots.length
    : null;
  // Every other draw zone gets re-based onto this game's own measured tee-line pace;
  // with no tee-line draws recorded yet, fall back to the middle of the target band.
  const teeLineReference = avgTeeLineWeight ?? (14.5 + 15.0) / 2;

  const awayCurlStats = away.length > 0 ? computeDeviationStats(averageByFoot(away)) : null;
  const homeCurlStats = home.length > 0 ? computeDeviationStats(averageByFoot(home)) : null;
  const curlByEndStats = computeDeviationStats(averageCurlByEnd(game));

  const awayFlatnessScore = away.length >= MIN_SHOTS_FOR_FLATNESS_SCORE
    ? tieredPathScore(awayCurlStats, CURL_PATH_RULE)
    : null;
  const homeFlatnessScore = home.length >= MIN_SHOTS_FOR_FLATNESS_SCORE
    ? tieredPathScore(homeCurlStats, CURL_PATH_RULE)
    : null;

  const awayWeightStats = awayWeightShots.length > 0
    ? computeDeviationStats(averageTimeByFoot(awayWeightShots, teeLineReference))
    : null;
  const homeWeightStats = homeWeightShots.length > 0
    ? computeDeviationStats(averageTimeByFoot(homeWeightShots, teeLineReference))
    : null;
  const awayWeightFlatnessScore = awayWeightShots.length >= MIN_SHOTS_FOR_FLATNESS_SCORE
    ? escalatingPathScore(awayWeightStats, WEIGHT_PATH_RULE)
    : null;
  const homeWeightFlatnessScore = homeWeightShots.length >= MIN_SHOTS_FOR_FLATNESS_SCORE
    ? escalatingPathScore(homeWeightStats, WEIGHT_PATH_RULE)
    : null;

  // Comparing away against home (shape or average) is only as trustworthy as
  // the shakier of the two sides — same MIN_SHOTS_FOR_FLATNESS_SCORE bar as
  // each side's own flatness score, just required of both at once.
  const curlBothSidesReliable = away.length >= MIN_SHOTS_FOR_FLATNESS_SCORE
    && home.length >= MIN_SHOTS_FOR_FLATNESS_SCORE;
  const weightBothSidesReliable = awayWeightShots.length >= MIN_SHOTS_FOR_FLATNESS_SCORE
    && homeWeightShots.length >= MIN_SHOTS_FOR_FLATNESS_SCORE;

  const curlShapeSimilarityScore = curlBothSidesReliable
    ? shapeSimilarityScore(awayCurlStats, homeCurlStats, 2.0)
    : null;
  const curlAverageSimilarityScore = curlBothSidesReliable
    ? averageDifferenceScore(awayCurlStats?.average ?? null, homeCurlStats?.average ?? null, 0.5)
    : null;
  const weightShapeSimilarityScore = weightBothSidesReliable
    ? shapeSimilarityScore(awayWeightStats, homeWeightStats, 1.0)
    : null;
  const weightAverageSimilarityScore = weightBothSidesReliable
    ? averageDifferenceScore(awayWeightStats?.average ?? null, homeWeightStats?.average ?? null, 0.25)
    : null;

  // Game-long consistency pools every end together rather than splitting by
  // away/home, so it needs the same total-shot bar applied to the pooled count
  // instead — same reasoning as above: too few shots make a trend line noise,
  // not signal.
  const endConsistencyScore = curlByEndStats && away.length + home.length >= MIN_SHOTS_FOR_FLATNESS_SCORE
    ? rmsToScore(rootMeanSquare(curlByEndStats.deviation.map(p => p.y)), 1.0)
    : null;

  // Weight's end-to-end check cares about something different from curl's:
  // not wobble around the trend line, but how much the trend line itself has
  // dropped from the first end to the last — a low-slope line is fine, a
  // steep drop isn't. Reconstructing the baseline at each end from raw minus
  // deviation avoids re-fitting it a second time.
  const weightByEndStats = computeDeviationStats(averageTimeByEnd(drawWeightShots, teeLineReference));
  const weightEndConsistencyScore = weightByEndStats && drawWeightShots.length >= MIN_SHOTS_FOR_FLATNESS_SCORE
    ? averageDifferenceScore(
        weightByEndStats.raw[0].y - weightByEndStats.deviation[0].y,
        weightByEndStats.raw[weightByEndStats.raw.length - 1].y - weightByEndStats.deviation[weightByEndStats.deviation.length - 1].y,
        0.5,
      )
    : null;

  const sheetAvgCurl = awayCurlStats && homeCurlStats
    ? (awayCurlStats.average + homeCurlStats.average) / 2
    : awayCurlStats?.average ?? homeCurlStats?.average ?? null;
  // The 4.5-5.0ft target is a distance, not a signed broom-relative offset — take the
  // magnitude, since sheetAvgCurl's sign only reflects which side/turn the shots
  // happened to be thrown from (see the other curl sub-scores above, which want that
  // sign preserved for flatness/shape comparisons and so don't do this).
  const averageCurlScore = sheetAvgCurl !== null ? targetBandScore(Math.abs(sheetAvgCurl), 4.5, 5.0, 2.5) : null;
  const averageWeightScore = avgTeeLineWeight !== null ? targetBandScore(avgTeeLineWeight, 14.5, 15.0, 1.25) : null;

  const curlSubScores: SheetSubScore[] = [
    { label: 'Away End Flatness', value: awayFlatnessScore, description: 'Away-end paths lose 1-2 points for variance off baseline, plus 3 for curl under 1 ft or over 7 ft.' },
    { label: 'Home End Flatness', value: homeFlatnessScore, description: 'Home-end paths lose 1-2 points for variance off baseline, plus 3 for curl under 1 ft or over 7 ft.' },
    { label: 'Consistent Shape (Away/Home)', value: curlShapeSimilarityScore, description: 'How closely the away and home curl-variance shapes match.' },
    { label: 'Consistent Curl (Away/Home)', value: curlAverageSimilarityScore, description: 'Under 0.5 ft gap between away and home average curl costs nothing; past that, a point per further 0.5 ft.' },
    { label: 'Game Long Consistent Curl', value: endConsistencyScore, description: 'How steady the average curl stays from end to end.' },
    { label: 'Average Curl Expectation', value: averageCurlScore, description: 'How close the sheet’s average curl sits to the 4.5–5.0 ft target.' },
  ];
  const weightSubScores: SheetSubScore[] = [
    { label: 'Away End Flatness', value: awayWeightFlatnessScore, description: 'Away-end paths lose a point per quarter-second of weight variance past 0.5s, plus 3 for draws under 12s or over 17s.' },
    { label: 'Home End Flatness', value: homeWeightFlatnessScore, description: 'Home-end paths lose a point per quarter-second of weight variance past 0.5s, plus 3 for draws under 12s or over 17s.' },
    { label: 'Consistent Shape (Away/Home)', value: weightShapeSimilarityScore, description: 'How closely the away and home weight-variance shapes match.' },
    { label: 'Consistent Weight (Away/Home)', value: weightAverageSimilarityScore, description: 'Under 0.25s gap between away and home average weight costs nothing; past that, a point per further 0.25s.' },
    { label: 'Game Long Consistent Weight', value: weightEndConsistencyScore, description: 'Under 0.5s of trend-line drop from first end to last costs nothing; past that, a point per further 0.5s.' },
    { label: 'Average Weight Expectation', value: averageWeightScore, description: 'How close the sheet’s average tee-line weight sits to the 14.5–15.0s target.' },
  ];
  const subScores = [...curlSubScores, ...weightSubScores];
  // Rebased to a flat 100 over however many sub-scores actually have a
  // determined value, not the full fixed list — a category with no value
  // yet (not enough shots to compute it) is excluded from both the sum and
  // the base entirely, rather than counted as if it scored a 0. Otherwise
  // 3 categories at a perfect 10/10 with the rest undetermined would read as
  // 30/120 (25%) instead of 30/30 (100%).
  const determinedSubScores = subScores.filter(
    (s): s is SheetSubScore & { value: number } => s.value !== null
  );
  const overallScore = determinedSubScores.length > 0
    ? (determinedSubScores.reduce((sum, s) => sum + s.value, 0) / (determinedSubScores.length * 10)) * 100
    : 0;

  const curlScoreValues = curlSubScores.map(s => s.value).filter((v): v is number => v !== null);
  const compositeCurlScore = curlScoreValues.length > 0
    ? curlScoreValues.reduce((sum, v) => sum + v, 0) / curlScoreValues.length
    : null;

  const weightScoreValues = weightSubScores.map(s => s.value).filter((v): v is number => v !== null);
  const compositeWeightScore = weightScoreValues.length > 0
    ? weightScoreValues.reduce((sum, v) => sum + v, 0) / weightScoreValues.length
    : null;

  return {
    hasData: away.length > 0 || home.length > 0,
    away, home,
    awayCurlStats, homeCurlStats, curlByEndStats,
    drawWeightShots, awayWeightShots, homeWeightShots,
    awayWeightStats, homeWeightStats, weightByEndStats,
    teeLineReference, avgTeeLineWeight,
    curlSubScores, weightSubScores, subScores,
    determinedSubScoreCount: determinedSubScores.length,
    overallScore, compositeCurlScore, compositeWeightScore,
  };
}
